/* eslint-disable prettier/prettier */
import { General } from "./constants";
import { getNUP } from "@/services/api/process/ApiProcess";
import { IProtocol } from "./utils-types";

const alnum = "[a-zA-Z0-9]";
const digit = "[0-9]";

const fullYear = new Date().getFullYear();
const currentYear = fullYear.toString();
const first2DigitsYear = currentYear.substring(0, 2);
const last2DigitsYear = currentYear.substring(2, 4);

export const NUP = {
  regexForTesting: new RegExp(
    `(${alnum}{5}).(${alnum}{6})/(${first2DigitsYear}${digit}{2})-(${alnum}{2})`
  ),
  regexForMask: new RegExp(
    `(${alnum}{5})(${alnum}{6})(${first2DigitsYear}${digit}{2})(${alnum}{2})`
  ),
  mask: "$1.$2/$3-$4",
  alnumWithoutMask: 17,
  description: "Número Único de Protocolo - NUP"
};

export const MPF_SAJMP = {
  regexForTesting: new RegExp(
    `(${alnum}{2}).(${first2DigitsYear}${digit}{2}).(${alnum}{8})-(${alnum}{1})`
  ),
  regexForMask: new RegExp(
    `(${alnum}{2})(${first2DigitsYear}${digit}{2})(${alnum}{8})(${alnum}{1})`
  ),
  mask: "$1.$2.$3-$4",
  alnumWithoutMask: 15,
  description: "Ministerio Publico Federal - SAJMP"
};

export const MUNICIPAL_SPU = {
  regexForTesting: new RegExp(`(${alnum}{6})/(${first2DigitsYear}${digit}{2})`),
  regexForMask: new RegExp(`(${alnum}{6})(${first2DigitsYear}${digit}{2})`),
  mask: "$1/$2",
  alnumWithoutMask: 10,
  description: "Prefeitura de Fortaleza - SPU"
};

export const SEI = {
  regexForTesting: new RegExp(
    `(${alnum}{5}).(${alnum}{6})/(${first2DigitsYear}${digit}{2})-(${alnum}{2})`
  ),
  regexForMask: new RegExp(
    `(${alnum}{5})(${alnum}{6})(${first2DigitsYear}${digit}{2})(${alnum}{2})`
  ),
  mask: "$1.$2/$3-$4",
  alnumWithoutMask: 17,
  description: "SEI"
};

export const SPU_STATE = {
  regexForTesting: new RegExp(`(${last2DigitsYear}${alnum}{6})-(${alnum}{1})`),
  regexForMask: new RegExp(`(${last2DigitsYear}${alnum}{6})(${alnum}{1})`),
  mask: "$1-$2",
  alnumWithoutMask: 9,
  description: "Estado - SPU"
};

export const TCE_SAP = {
  regexForTesting: new RegExp(
    `(${alnum}{5})/(${first2DigitsYear}${digit}{2})-(${alnum}{1})`
  ),
  regexForMask: new RegExp(
    `(${alnum}{5})(${first2DigitsYear}${digit}{2})(${alnum}{1})`
  ),
  mask: "$1/$2-$3",
  alnumWithoutMask: 10,
  description: "TCE - SAP"
};

export const TJ_SAJADM_CPA = {
  regexForTesting: new RegExp(
    `(${alnum}{7})-(${alnum}{2}).(${alnum}{4}).(${alnum}{1}).(${alnum}{2}).(${alnum}{4})`
  ),
  regexForMask: new RegExp(
    `(${alnum}{7})(${alnum}{2})(${alnum}{4})(${alnum}{1})(${alnum}{2})(${alnum}{4})`
  ),
  mask: "$1-$2.$3.$4.$5.$6",
  alnumWithoutMask: 20,
  description: "TJ - SAJADM-CPA"
};

export const VDOC = {
  regexForTesting: new RegExp(`(${alnum}{5})/(${first2DigitsYear}${digit}{2})`),
  regexForMask: new RegExp(`(${alnum}{5})(${first2DigitsYear}${digit}{2})`),
  mask: "$1/$2",
  alnumWithoutMask: 9,
  description: "Assembléia - VDOC"
};

export const VIPROC = {
  regexForTesting: new RegExp(`(${alnum}{8})/(${first2DigitsYear}${digit}{2})`),
  regexForMask: new RegExp(`(${alnum}{8})(${first2DigitsYear}${digit}{2})`),
  mask: "$1/$2",
  alnumWithoutMask: 12,
  description: "VIPROC"
};

export const applyMask = (protocol: IProtocol, value: string) => {
  return value && value.replace(protocol.regexForMask, protocol.mask);
};

export const isValidProtocol = (protocol: IProtocol, value: string) => {
  const mask = applyMask(protocol, value);
  return (
    value &&
    value.length === protocol.alnumWithoutMask &&
    protocol.regexForTesting.test(mask)
  );
};

export const getValueRaw = (value: string) => {
  return value && value.replace(/(\.|\/|-|\s)/g, "");
};

const protocolResult = (protocol: IProtocol, value: string) => {
  return {
    valueMask: applyMask(protocol, value),
    valueRaw: getValueRaw(value),
    description: `${General.PROTOCOL_OFTHE}: ${protocol.description}`
  };
};

const protocolWithoutStandardResult = (desc: string, value: string) => {
  return {
    valueMask: value,
    valueRaw: getValueRaw(value),
    description: desc
  };
};

const getProtocolResult = (value: string) => {
  let result = {};
  if (isValidProtocol(MPF_SAJMP, value)) {
    result = protocolResult(MPF_SAJMP, value);
  } else if (isValidProtocol(MUNICIPAL_SPU, value)) {
    result = protocolResult(MUNICIPAL_SPU, value);
  } else if (isValidProtocol(SEI, value)) {
    result = protocolResult(SEI, value);
  } else if (isValidProtocol(SPU_STATE, value)) {
    result = protocolResult(SPU_STATE, value);
  } else if (isValidProtocol(TCE_SAP, value)) {
    result = protocolResult(TCE_SAP, value);
  } else if (isValidProtocol(TJ_SAJADM_CPA, value)) {
    result = protocolResult(TJ_SAJADM_CPA, value);
  } else if (isValidProtocol(VDOC, value)) {
    result = protocolResult(VDOC, value);
  } else if (isValidProtocol(VIPROC, value)) {
    result = protocolResult(VIPROC, value);
  } else {
    result = protocolWithoutStandardResult(General.UNMAPPED_PROTOCOL, value);
  }
  return result;
};

export const getProtocolMask = async (value: string) => {
  const newValue = getValueRaw(value);
  const maxValue = 32;
  if (newValue && newValue.length > 0 && newValue.length <= maxValue) {
    const results = await getNUP(newValue);
    if (results.data && isValidProtocol(NUP, newValue)) {
      return {
        valueMask: applyMask(NUP, results.data.nup),
        valueRaw: getValueRaw(results.data.nup),
        description: results.data.subject.name
      };
    } else {
      return getProtocolResult(newValue);
    }
  } else {
    return protocolWithoutStandardResult(General.UNMAPPED_PROTOCOL, value);
  }
};
